import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Building2, MapPin, Phone, Mail, CheckCircle, XCircle, Ban, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Pending': { bg: 'bg-[#FFFBF0]', text: 'text-[#92600a]', border: 'border-[#D4AF37]/40' },
  'Active': { bg: 'bg-[#F0FFF4]', text: 'text-[#15803D]', border: 'border-green-200' },
  'Suspended': { bg: 'bg-[#FEF2F2]', text: 'text-[#D71920]', border: 'border-red-200' },
  'Rejected': { bg: 'bg-[#F7F7F7]', text: 'text-[#777]', border: 'border-[#E5E5E5]' },
  'Archived': { bg: 'bg-[#F7F7F7]', text: 'text-[#555]', border: 'border-[#E5E5E5]' },
};

const SchoolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [approvalInfo, setApprovalInfo] = useState(null);
  const [showCredentials, setShowCredentials] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchSchool(); }, [id]);

  const fetchSchool = async () => {
    try {
      const response = await axios.get(`${API_URL}/schools/${id}`);
      setSchool(response.data);
      if (response.data.status === 'Active') {
        try {
          const approvalRes = await axios.get(`${API_URL}/approvals/school/${id}`);
          setApprovalInfo(approvalRes.data);
        } catch { /* no approval info */ }
      }
    } catch { toast.error('Gagal memuatkan maklumat perguruan'); } finally { setLoading(false); }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/schools/${id}/status`, { status: newStatus, remarks: `Status dikemaskini kepada ${newStatus}` });
      if (newStatus === 'Active') { toast.success('Perguruan diluluskan! Akaun SCHOOL_ADMIN dicipta.'); setShowCredentials(true); }
      else if (newStatus === 'Rejected') toast.success('Permohonan ditolak');
      else toast.success('Perguruan digantung');
      fetchSchool();
    } catch { toast.error('Gagal mengemaskini status'); } finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== 'PADAM') { toast.error('Sila taip "PADAM"'); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/schools/${id}`, { data: { confirmation: deleteConfirmation } });
      toast.success('Perguruan dipadam');
      navigate('/admin/schools');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memadam perguruan');
    } finally { setDeleting(false); setDeleteDialogOpen(false); setDeleteConfirmation(''); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#999] animate-pulse">Memuatkan...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!school) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-sm text-[#777]">Perguruan tidak dijumpai</p>
        </div>
      </AdminLayout>
    );
  }

  const sc = statusConfig[school.status] || statusConfig['Rejected'];

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-5xl">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/schools')}
            className="p-2 rounded border border-[#E5E5E5] text-[#555] hover:bg-[#F7F7F7] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#0B0B0B]" data-testid="school-name">{school.name}</h1>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>{school.status}</span>
              {school.is_deleted && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-black/10 text-black/60 border border-black/20"><Trash2 size={9} className="inline mr-0.5" />Dipadam</span>}
            </div>
            <p className="text-sm text-[#777] mt-0.5">{school.division}, {school.district}</p>
          </div>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {/* Credentials Alert */}
        {showCredentials && approvalInfo?.temp_password && (
          <Alert className="bg-[#F0FFF4] border-2 border-green-300">
            <CheckCircle size={16} className="text-green-600" />
            <AlertDescription>
              <p className="font-bold text-green-800 mb-2">Perguruan diluluskan! Maklumat Login:</p>
              <div className="bg-white rounded border border-green-200 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#777]">Emel:</span>
                  <span className="font-bold text-[#0B0B0B]">{school.email}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#777]">Kata Laluan Sementara:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold font-mono text-[#0B0B0B]">{approvalInfo.temp_password}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(approvalInfo.temp_password); toast.success('Disalin'); }}
                      className="p-1 rounded hover:bg-green-100 text-green-600"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        {school.status === 'Pending' && (
          <div className="bg-[#FFFBF0] border border-[#D4AF37]/40 rounded p-4">
            <p className="text-sm text-[#7a6200] font-medium mb-3">Permohonan ini memerlukan kelulusan anda</p>
            <div className="flex flex-wrap gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="flex items-center gap-1.5 bg-[#16a34a] hover:bg-[#15803D] text-white text-sm font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50" disabled={updating} data-testid="approve-button">
                    <CheckCircle size={14} /> Luluskan
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Luluskan Perguruan</AlertDialogTitle><AlertDialogDescription>Luluskan perguruan ini? Akaun SCHOOL_ADMIN akan dicipta.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => handleStatusUpdate('Active')} className="bg-[#16a34a]">Ya, Luluskan</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50" disabled={updating} data-testid="reject-button">
                    <XCircle size={14} /> Tolak
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Tolak Perguruan</AlertDialogTitle><AlertDialogDescription>Tolak permohonan ini?</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => handleStatusUpdate('Rejected')} className="bg-red-600">Ya, Tolak</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {school.status === 'Active' && (
          <div className="flex flex-wrap justify-end gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50" disabled={updating} data-testid="suspend-button">
                  <Ban size={14} /> Gantung Perguruan
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Gantung Perguruan</AlertDialogTitle><AlertDialogDescription>Perguruan tidak akan dapat mendaftar ahli baru.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => handleStatusUpdate('Suspended')} className="bg-red-600">Ya, Gantung</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="flex items-center gap-1.5 border border-[#E5E5E5] text-[#555] hover:bg-[#F7F7F7] text-sm font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50" disabled={updating} data-testid="archive-button">
                  <Ban size={14} /> Arkibkan
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Arkibkan Perguruan</AlertDialogTitle><AlertDialogDescription>Perguruan yang diarkibkan tidak boleh login.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => handleStatusUpdate('Archived')} className="bg-gray-600">Ya, Arkibkan</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* School Details */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Maklumat Perguruan</div>
            <div className="flex justify-center mb-5">
              {school.logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${school.logo_url}`} alt={school.name} className="w-28 h-28 object-contain rounded border border-[#E5E5E5]" />
              ) : (
                <div className="w-28 h-28 rounded bg-[#D71920]/8 flex items-center justify-center border border-dashed border-[#E5E5E5]">
                  <Building2 size={36} className="text-[#D71920]" />
                </div>
              )}
            </div>
            <div className="space-y-3">
              {[
                { label: 'Ketua Jurulatih', value: school.head_coach_name },
                { label: 'Bahagian', value: school.division },
                { label: 'Daerah', value: school.district },
              ].map((item, i) => (
                <div key={i}>
                  <p className="text-xs text-[#999]">{item.label}</p>
                  <p className="text-sm font-semibold text-[#0B0B0B]">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Maklumat Hubungan</div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-[#D71920] flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#999]">Telefon</p>
                  <p className="text-sm font-semibold text-[#0B0B0B]">{school.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#D71920] flex-shrink-0" />
                <div>
                  <p className="text-xs text-[#999]">Emel</p>
                  <p className="text-sm font-semibold text-[#0B0B0B]">{school.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#D71920] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-[#999]">Alamat Latihan</p>
                  <p className="text-sm font-semibold text-[#0B0B0B]">{school.training_address}</p>
                </div>
              </div>
              {school.gelanggang && (
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="text-[#D71920] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-[#999]">Nama Gelanggang</p>
                    <p className="text-sm font-semibold text-[#0B0B0B]">{school.gelanggang}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete */}
        {!school.is_deleted && (
          <div className="flex justify-end">
            <button
              onClick={() => setDeleteDialogOpen(true)}
              className="flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded transition-colors"
              data-testid="delete-button"
            >
              <Trash2 size={14} /> Padam Perguruan
            </button>
          </div>
        )}

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">Padam Perguruan</DialogTitle>
              <DialogDescription className="space-y-2">
                <p>Anda akan memadam <strong>{school.name}</strong>. Ini akan memadam semua ahli dan akaun perguruan.</p>
                <p className="text-sm">Taip <strong className="text-red-700">PADAM</strong> untuk sahkan:</p>
              </DialogDescription>
            </DialogHeader>
            <Input
              value={deleteConfirmation}
              onChange={e => setDeleteConfirmation(e.target.value.toUpperCase())}
              className="border-red-200"
              placeholder="Taip PADAM"
              data-testid="delete-confirmation-input"
            />
            <DialogFooter>
              <button onClick={() => { setDeleteDialogOpen(false); setDeleteConfirmation(''); }} className="px-4 py-2 text-sm border border-[#E5E5E5] rounded hover:bg-[#F7F7F7]">Batal</button>
              <button onClick={handleDelete} disabled={deleteConfirmation !== 'PADAM' || deleting} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50" data-testid="confirm-delete-button">
                {deleting ? 'Memadam...' : 'Padam'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default SchoolDetail;
